import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Create a no-op storage for server-side rendering
const noopStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

export interface QuizItem {
  question_uid: string;
  selected_options: boolean | string | string[];
  qindex: number;
  isSubmitted?: boolean;
}

export interface AssessmentData {
  questions?: any[];
  quiz?: QuizItem[];
  started?: boolean;
  result?: any;
  quizData?: any; // Store randomized quiz questions for persistence
  /** Lesson quiz: correct answer per submitted question uid, from the checkQuizAnswer server action. */
  answerChecks?: Record<string, string | string[] | boolean | null>;
}

interface AssessmentStore {
  userEmail: string;
  assessments: Record<string, AssessmentData>;
  quiz: QuizItem[];
  setUserEmail: (email: string) => void;
  setAssessmentQuestions: (uid: string, questions: any[]) => void;
  setAssessmentStarted: (uid: string, value?: boolean) => void;
  setAssessmentResult: (uid: string, result: any) => void;
  clearAssessment: (uid: string) => void;
  clearAssessmentResult: () => void;
  clearAssessmentExceptResult: (uid: string) => void;
  clearAllAssessments: () => void;
      setQuizData: (uid: string, data: Partial<AssessmentData> | QuizItem[]) => void;
  setQuiz: (uid: string, newQuiz: QuizItem) => void;
      setSubmittedQuiz: (qindex: number) => void;
  clearQuiz: () => void;
}

export const useAssessmentData = create<AssessmentStore>()(
  persist(
    (set, get) => ({
      userEmail: '',
      assessments: {},
      quiz: [],
      setUserEmail: (email: string) => {
        const prevEmail = get()?.userEmail;
        set({ userEmail: email });
        if (!email || prevEmail !== email) {
          // Clear all assessment data when user logs out or email changes
          set({ assessments: {}, quiz: [] });
        }
      },
      setAssessmentQuestions: (uid: string, questions: any[]) => {
        set((state) => ({
          assessments: {
            ...state.assessments,
            [uid]: {
              ...state.assessments[uid],
              questions: questions,
            },
          },
        }));
      },
      setAssessmentStarted: (uid: string, value?: boolean) => {
        set((state) => ({
          assessments: {
            ...state.assessments,
            [uid]: {
              ...state.assessments[uid],
              started: value,
            },
          },
        }));
      },
      setAssessmentResult: (uid: string, result: any) => {
        set((state) => ({
          assessments: {
            ...state.assessments,
            [uid]: {
              ...state.assessments[uid],
              result: result,
            },
          },
        }));
      },
      clearAssessment: (uid: string) => {
        set((state) => {
          const { [uid]: _, ...remainingAssessments } = state.assessments;
          return { assessments: remainingAssessments };
        });
      },
      clearAssessmentResult: () => {
        set((state) => ({
          assessments: Object.fromEntries(
            Object.entries(state.assessments).map(([key, value]) => [
              key,
              { ...value, result: null },
            ])
          ),
        }));
      },
      clearAssessmentExceptResult: (uid: string) => {
        set((state) => ({
          assessments: {
            ...state.assessments,
            [uid]: {
              result: state.assessments[uid]?.result || null,
            },
          },
        }));
      },
      clearAllAssessments: () => {
        set({ assessments: {} });
      },
      setQuizData: (uid: string, data: Partial<AssessmentData> | QuizItem[]) => {
        set((state) => {
          // If data is an array, treat it as quiz array (backward compatibility)
          if (Array.isArray(data)) {
            return {
              assessments: {
                ...state.assessments,
                [uid]: {
                  ...state.assessments[uid],
                  quiz: data, // data is already an array
                },
              },
            };
          }
          // Otherwise, treat it as Partial<AssessmentData>
          // Ensure quiz property is always an array if present
          const updatedData = { ...data };
          if ('quiz' in updatedData && updatedData.quiz !== undefined) {
            updatedData.quiz = Array.isArray(updatedData.quiz) ? updatedData.quiz : [];
          }
          return {
            assessments: {
              ...state.assessments,
              [uid]: {
                ...state.assessments[uid],
                ...updatedData,
              },
            },
          };
        });
      },
      setQuiz: (uid: string, newQuiz: QuizItem) => {
        set((state) => {
          // Use global quiz array like Academy 1.0
          const currentQuiz = Array.isArray(state.quiz) ? state.quiz : [];
          
          const existingIndex = currentQuiz.findIndex(
            (q) => q.question_uid === newQuiz.question_uid
          );

          let updatedQuiz: QuizItem[];
          if (existingIndex !== -1) {
            // Update existing question
            updatedQuiz = [...currentQuiz];
            updatedQuiz[existingIndex] = newQuiz;
          } else {
            // Add new question
            updatedQuiz = [...currentQuiz, newQuiz];
          }

          return { quiz: updatedQuiz };
        });
      },
      setSubmittedQuiz: (qindex: number) => {
        set((state) => {
          // Use global quiz array like Academy 1.0
          const currentQuiz = Array.isArray(state.quiz) ? state.quiz : [];
          if (currentQuiz.length === 0) {
            return state;
          }

          const updatedQuiz = currentQuiz.map((quizItem) => {
            if (quizItem.qindex === qindex) {
              return { ...quizItem, isSubmitted: true };
            }
            return quizItem;
          });

          return { quiz: updatedQuiz };
        });
      },
      clearQuiz: () => {
        set({ quiz: [] });
      },
    }),
    {
      name: 'assessment',
      storage: createJSONStorage(() => (typeof window !== 'undefined' ? localStorage : noopStorage)),
    }
  )
);

